import React, { useState } from "react";
import useSiteContent from "@/lib/useSiteContent";
import { motion } from "framer-motion";
import { Sun, Moon, Sparkles, Calendar, Stethoscope, GraduationCap, MessageCircle } from "lucide-react";

/**
 * DualPillar — the v0.4 positioning section (graphic and interactive).
 * Dark cosmic background, glowing halos, two interactive pillars (☀/☾)
 * and an animated client journey linking the two poles.
 */
export default function DualPillar() {
  const { text } = useSiteContent();
  const [active, setActive] = useState(null); // "treat" | "teach" | null

  const journey = [
    { key: "j1", icon: Calendar, tone: "treat", label: text("dualpillar.j1") },
    { key: "j2", icon: Stethoscope, tone: "treat", label: text("dualpillar.j2") },
    { key: "j3", icon: GraduationCap, tone: "teach", label: text("dualpillar.j3") },
    { key: "j5", icon: MessageCircle, tone: "treat", label: text("dualpillar.j5") },
  ];

  const Pillar = ({ id, icon: Icon, title, modules, glow, ring, iconColor }) => (
    <motion.div
      onMouseEnter={() => setActive(id)}
      onMouseLeave={() => setActive(null)}
      whileHover={{ y: -8 }}
      data-testid={`pillar-${id}`}
      className={`relative flex-1 rounded-[28px] p-8 border backdrop-blur-sm cursor-default transition-colors duration-500 ${
        active === id ? `${ring} bg-white/[0.06]` : "border-white/10 bg-white/[0.03]"
      }`}
    >
      <div className={`absolute -inset-px rounded-[28px] opacity-0 blur-2xl transition-opacity duration-500 ${glow} ${active === id ? "opacity-40" : ""}`} />
      <div className="relative">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-5 ${iconColor}`}>
          <Icon className="w-8 h-8" strokeWidth={1.5} />
        </div>
        <div className="font-sol font-semibold text-2xl text-sol-ivory">{title}</div>
        <div className="flex flex-wrap gap-2 mt-4">
          {modules.split("·").map((m) => (
            <span key={m} className="text-xs px-3 py-1 rounded-full bg-white/[0.06] text-sol-ivory/70 border border-white/10">{m.trim()}</span>
          ))}
        </div>
      </div>
    </motion.div>
  );

  return (
    <section className="relative py-28 lg:py-36 bg-sol-midnight overflow-hidden" data-testid="dualpillar-section">
      {/* Glowing halos */}
      <div className="absolute top-0 left-1/4 w-[34rem] h-[34rem] rounded-full bg-sol-gold/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[34rem] h-[34rem] rounded-full bg-sol-sage/10 blur-[120px] pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-sol-gold mb-4">
            <span className="text-sol-gold">☀</span>
            {text("dualpillar.eyebrow")}
            <span className="text-sol-sage">☾</span>
          </div>
          <h2 className="font-display text-4xl lg:text-5xl text-sol-ivory leading-tight">
            {text("dualpillar.title")}
          </h2>
        </div>

        {/* Orbit diagram */}
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div className="flex flex-col items-center">
            {/* Solunary orb */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative mb-8"
            >
              <div className="absolute inset-0 rounded-full bg-sol-gradient blur-xl opacity-60 animate-pulse" />
              <div className="relative px-7 py-3 rounded-full bg-sol-gradient text-sol-midnight font-sol font-bold tracking-wide shadow-lg">
                SOLUNARY
              </div>
            </motion.div>

            {/* Connector */}
            <div className="w-px h-8 bg-gradient-to-b from-sol-gold/50 to-transparent" />

            {/* Two pillars */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="flex gap-4 w-full"
            >
              <Pillar
                id="treat"
                icon={Sun}
                title={text("dualpillar.treat")}
                modules={text("dualpillar.treatModules")}
                glow="bg-sol-gold"
                ring="border-sol-gold/50"
                iconColor="bg-sol-gold/15 text-sol-gold"
              />
              <Pillar
                id="teach"
                icon={Moon}
                title={text("dualpillar.teach")}
                modules={text("dualpillar.teachModules")}
                glow="bg-sol-sage"
                ring="border-sol-sage/50"
                iconColor="bg-sol-sage/15 text-sol-sage"
              />
            </motion.div>

            {/* Connector */}
            <div className="w-px h-8 bg-gradient-to-b from-transparent to-sol-sage/40" />

            {/* Intelligence base */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-white/[0.04] border border-white/10 text-sol-ivory"
            >
              <Sparkles className="w-4 h-4 text-sol-gold" />
              <span className="text-sm font-medium">{text("dualpillar.intelligence")}</span>
            </motion.div>
          </div>

          {/* Copy + animated journey */}
          <div>
            <p className="text-lg text-sol-ivory/75 leading-relaxed mb-8">
              {text("dualpillar.body")}
            </p>

            <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
              <div className="text-xs uppercase tracking-wider text-sol-ivory/50 mb-4">
                {text("dualpillar.journeyLabel")}
              </div>
              <div className="flex items-center">
                {journey.map((step, i) => {
                  const Icon = step.icon;
                  const toneColor = step.tone === "treat" ? "text-sol-gold" : "text-sol-sage";
                  const toneBg = step.tone === "treat" ? "bg-sol-gold/15" : "bg-sol-sage/15";
                  return (
                    <React.Fragment key={step.key}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.6 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.15 * i }}
                        className="flex flex-col items-center gap-1.5 flex-shrink-0"
                        data-testid={`journey-${step.key}`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${toneBg} ${toneColor}`}>
                          <Icon className="w-5 h-5" strokeWidth={1.6} />
                        </div>
                        <span className="text-[10px] text-sol-ivory/60 text-center w-16 leading-tight">{step.label}</span>
                      </motion.div>
                      {i < journey.length - 1 && (
                        <motion.div
                          initial={{ scaleX: 0 }}
                          whileInView={{ scaleX: 1 }}
                          viewport={{ once: true }}
                          transition={{ delay: 0.15 * i + 0.1, duration: 0.4 }}
                          className="flex-1 h-px origin-left bg-gradient-to-r from-sol-gold/40 to-sol-sage/40 mb-5"
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
